import React, { useContext, useState, useEffect } from "react";
import { auth } from "../../firebase/firebase";
import { GoogleAuthProvider } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { accountApiEntryPoint, argosApiEntryPoint } from "../../GlobalConfig";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isPaidIndividualCustomer, setIsPaidIndividualCustomer] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [isEmailUser, setIsEmailUser] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);
  const [authContextLoaded, setAuthContextLoaded] = useState(false);
  const [isInstitutionalAccount, setIsInstitutionalAccount] = useState(false);
  const [isCountryWideAccount, setIsCountryWideAccount] = useState(false);
  const [isPublisherAccount, setIsPublisherAccount] = useState(false);
  const [publisherId, setPublisherId] = useState(null);
  const [isJournalistAccount, setIsJournalistAccount] = useState(false);
  const [isDemoAccount, setIsDemoAccount] = useState(false);

  const [authToken, setAuthToken] = useState(null);
  const [countryRoles, setCountryRoles] = useState([]);
  const [institutionalRoles, setInstitutionalRoles] = useState([]);
  const [mainInstitutionDisplayName, setMainInstitutionDisplayName] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, initializeUser);
    return unsubscribe;
  }, []);

  async function initializeUser(user) {
    if (user) {
      await auth.currentUser.getIdTokenResult(true).then((idTokenResult) => {
        setAuthToken(idTokenResult.token)
        const accountType = idTokenResult.claims.accountType
        if (accountType === undefined || accountType === "BASIC") {
          setIsDemoAccount(true)
          setIsInstitutionalAccount(false)
          setIsPublisherAccount(false)
          setIsJournalistAccount(false)
        } else if (accountType === "PUBLISHER") {
          setIsDemoAccount(false)
          setIsPublisherAccount(true)
          setIsJournalistAccount(false)
          const publisherIds = idTokenResult.claims.publishers?.split(",")
          setPublisherId(publisherIds[0])
        } else if (accountType === "PUBLISHER_DEMO") {
          setIsDemoAccount(true)
          setIsPublisherAccount(true)
          setIsJournalistAccount(false)
          const publisherIds = idTokenResult.claims.publishers?.split(",")
          setPublisherId(publisherIds[0])
        } else if (accountType === "JOURNALIST") {
          setIsDemoAccount(false)
          setIsPublisherAccount(false)
          setIsJournalistAccount(true)
        } else if (accountType === "SLEUTH") {
          setIsDemoAccount(false)
          setIsPublisherAccount(false)
          setIsJournalistAccount(true)
        } else if (accountType === "INSTITUTIONAL_DEMO") {
          setIsDemoAccount(true)
          setIsJournalistAccount(false)
          setIsPublisherAccount(false)
        } else if (accountType === "INSTITUTIONAL") {
          setIsDemoAccount(false)
          setIsJournalistAccount(false)
          setIsPublisherAccount(false)
        } else if (accountType === "INSTITUTION_AND_PUBLISHER_DEMO") {
          setIsDemoAccount(true)
          setIsJournalistAccount(false)
          setIsPublisherAccount(true)
        } else if (accountType === "INSTITUTION_AND_PUBLISHER") {
          setIsDemoAccount(false)
          setIsJournalistAccount(false)
          setIsPublisherAccount(true)
        }

        const customerId = idTokenResult.claims.customerId
        setCustomerId(customerId)
        if (customerId) {
          setIsPaidIndividualCustomer(true)
          setIsDemoAccount(false)
        }

        setCurrentUser({ ...user });        

        // check if provider is email and password login
        const isEmail = user.providerData.some(
          (provider) => provider.providerId === "password"
        );
        setIsEmailUser(isEmail);

        // check if the auth provider is google or not
        const isGoogle = user.providerData.some(
          (provider) => provider.providerId === GoogleAuthProvider.PROVIDER_ID
        );
        setIsGoogleUser(isGoogle);
        setUserLoggedIn(true);
        setUserLoaded(true)

        async function fetchData() {
          fetch(accountApiEntryPoint + '/roles'
            , {
              method: 'GET',
              headers: {
                'content-type': 'application/json;charset=UTF-8',
                'Authorization': 'Bearer ' + idTokenResult.token
              }
            })
            .then((response) => {
              if (response.status === 200) {
                response.json().then(data => {
                  const institutionRoles = Array.from(new Set(data.institutionalRoles?.map(item => [item.institutionId, item.role])));
                  const countryRoles = Array.from(new Set(data.countryRoles?.map(item => [item.countryCode, item.role])));

                  if(countryRoles && countryRoles.length > 0){
                    setCountryRoles(countryRoles)
                    setIsCountryWideAccount(true)
                    setIsDemoAccount(false)
                  }

                  if (institutionRoles && institutionRoles.length > 0) {
                    setInstitutionalRoles(institutionRoles);
                    setIsInstitutionalAccount(true)
                    setIsDemoAccount(false)
                    fetch(argosApiEntryPoint + '/institution/' + institutionRoles[0][0].replaceAll("https://openalex.org/", "")
                      , {
                        method: 'GET',
                        headers: {
                          'content-type': 'application/json;charset=UTF-8',
                          'Authorization': 'Bearer ' + idTokenResult.token
                        }
                      })
                      .then((response) => response.json())
                      .then((data) => {
                        setMainInstitutionDisplayName(data.displayName)
                        setAuthContextLoaded(true);
                      })
                  } else {
                    setIsInstitutionalAccount(false)
                    setAuthContextLoaded(true);
                  }
                })

              } else {
                setIsInstitutionalAccount(false)
                setAuthContextLoaded(true);
              }
            }
            )
        }
        fetchData()
      }
      );


    } else {
      setCurrentUser(null);
      setUserLoggedIn(false);
      setUserLoaded(true)
      setAuthContextLoaded(true);
    }


  }

  const value = {
    authToken,
    userLoggedIn,
    isEmailUser,
    isGoogleUser,
    currentUser,
    setCurrentUser,
    isInstitutionalAccount,
    isCountryWideAccount,
    isPublisherAccount,
    publisherId,
    isJournalistAccount,
    isDemoAccount,
    isPaidIndividualCustomer,
    customerId,
    institutionalRoles,
    countryRoles,
    mainInstitutionDisplayName,
    authContextLoaded
  }

  return (
    <AuthContext.Provider value={value}>
      {userLoaded && children}
    </AuthContext.Provider>
  );
}